import React from "react";
import { Minus, Plus } from "lucide-react";
import { truthy } from "../../lib/packageLogic";
import { HelpIcon, HelpPanel } from "../package/QuestionHelp";

const INPUT =
  "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-orange/40 focus:border-orange transition-colors";

const Stepper = ({ id, testId, value, onChange }) => {
  const n = Number(value) || 0;
  const step = (by) => onChange(Math.max(0, n + by));
  return (
    <div className="flex items-stretch rounded-xl border border-slate-200 bg-white overflow-hidden focus-within:ring-2 focus-within:ring-orange/40 focus-within:border-orange transition-colors">
      <input
        id={id}
        data-testid={testId}
        type="number"
        min="0"
        inputMode="numeric"
        className="flex-1 min-w-0 px-4 py-3 text-sm text-navy focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
      />
      <button
        type="button"
        onClick={() => step(-1)}
        aria-label="One fewer"
        data-testid={`${testId}-minus`}
        className="px-3.5 border-l border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-navy transition-colors"
      >
        <Minus size={15} />
      </button>
      <button
        type="button"
        onClick={() => step(1)}
        aria-label="One more"
        data-testid={`${testId}-plus`}
        className="px-3.5 border-l border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-navy transition-colors"
      >
        <Plus size={15} />
      </button>
    </div>
  );
};

// One question: title, optional ⓘ help, the control the pricing platform asked for.
export const PricingField = ({ question: q, value, onChange, highlight, openHelp, onToggleHelp }) => {
  const id = `pj-q-${q.key}`;
  const testId = `pricing-q-${q.key}`;
  const helpOpen = openHelp === q.key;

  const control = () => {
    switch (q.input_type) {
      case "dropdown":
        return (
          <select id={id} data-testid={testId} className={INPUT} value={value ?? ""} onChange={(e) => onChange(q.key, e.target.value)}>
            <option value="">Please choose…</option>
            {(q.options || []).map((o) => (
              <option key={o.key} value={o.key}>{o.label}</option>
            ))}
          </select>
        );
      case "yes_no":
        return (
          <div className="flex gap-2" data-testid={testId}>
            {[["Yes", true], ["No", false]].map(([label, val]) => {
              const on = value !== undefined && value !== "" && truthy(value) === val;
              return (
                <button
                  key={label}
                  type="button"
                  data-testid={`${testId}-${label.toLowerCase()}`}
                  onClick={() => onChange(q.key, val)}
                  className={`px-7 py-2.5 rounded-xl text-sm font-bold border transition-colors ${
                    on ? "bg-orange text-white border-orange" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        );
      case "number":
        return <Stepper id={id} testId={testId} value={value} onChange={(v) => onChange(q.key, v)} />;
      case "currency":
        return (
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-sm">£</span>
            <input
              id={id}
              data-testid={testId}
              type="number"
              min="0"
              inputMode="numeric"
              className={`${INPUT} pl-8`}
              value={value ?? ""}
              onChange={(e) => onChange(q.key, e.target.value === "" ? "" : Number(e.target.value))}
            />
          </div>
        );
      default:
        return (
          <input id={id} data-testid={testId} type="text" className={INPUT} value={value ?? ""} onChange={(e) => onChange(q.key, e.target.value)} />
        );
    }
  };

  return (
    <div
      id={`pj-field-${q.key}`}
      data-q-key={q.key}
      className={`rounded-xl p-3 -mx-3 scroll-mt-32 transition-colors ${highlight ? "bg-orange/10 ring-1 ring-orange/40" : ""}`}
    >
      <div className="flex items-start gap-1.5">
        <label htmlFor={id} className="block text-sm font-semibold text-navy">
          {q.label}
          {q.required && <span className="text-orange ml-1">*</span>}
        </label>
        <HelpIcon qKey={q.key} help={q.help} open={helpOpen} onToggle={onToggleHelp} />
      </div>
      {helpOpen && <HelpPanel qKey={q.key} help={q.help} onClose={() => onToggleHelp(null)} />}
      {q.help_text && <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{q.help_text}</p>}
      <div className="mt-2.5">{control()}</div>
      {highlight && <p className="text-xs font-semibold text-orange-dark mt-2">We need this one to price your package.</p>}
    </div>
  );
};

export default PricingField;
